import { Close } from "@mui/icons-material";
import { Grid, IconButton } from "@mui/material";
import Markdown from "markdown-to-jsx";
import React from "react";
import { AcxGradientTypography } from "./AcxGradientTypography";
import {
    AcxLLMReasoningFeedback,
    AcxLLMReasoningFeedbackDivider,
    AcxLLMReasoningFeedbackProps,
} from "./AcxLLMReasoningFeedback";

type AcxLLMReasoningProps = {
    reasoning: string;
    onClose?: () => void;
    getTitle?: (
        llmReasoningTitleTypography: React.ReactNode,
    ) => React.ReactNode;
    /**
     * Renders agree / disagree feedback on the reasoning beneath the body.
     * Omit to leave it out entirely -- not every surface showing reasoning
     * collects feedback on it.
     */
    feedback?: AcxLLMReasoningFeedbackProps;
};

export function AcxLLMReasoning(props: AcxLLMReasoningProps) {
    const Title = (
        <Grid item>
            <AcxGradientTypography textAlign="center" sx={{ fontSize: "13px" }}>
                Model Reasoning <SmallSparklingStar />
            </AcxGradientTypography>
        </Grid>
    );

    return (
        <Grid
            container
            item
            direction="column"
            flexWrap="nowrap"
            rowGap={props.feedback ? 1.5 : 0}
            sx={(theme) => ({
                paddingInline: theme.spacing(2),
                paddingBlock: theme.spacing(2),
                // Radius applies to both layers, so the gradient follows the corners.
                borderRadius: theme.shape.borderRadius / 2,
                background: `
                    linear-gradient(${theme.palette.gray[50]}, ${theme.palette.gray[50]}) padding-box,
                    linear-gradient(131deg, #32D1B7 3.43%, #349AC7 70.89%, #3664D6 100.5%) border-box
                `,
                border: "1.5px solid transparent",
            })}
        >
            <Grid
                container
                item
                justifyContent="space-between"
                alignItems="center"
                flexWrap="nowrap"
            >
                <Grid
                    container
                    item
                    width="fit-content"
                    flexWrap="nowrap"
                    alignItems="center"
                    columnGap={1}
                >
                    {props.getTitle ? props.getTitle?.(Title) : Title}
                </Grid>
                {props.onClose && (
                    <Grid item>
                        <IconButton
                            size="small"
                            onClick={() => props.onClose?.()}
                        >
                            <Close fontSize="small" />
                        </IconButton>
                    </Grid>
                )}
            </Grid>
            <Grid item>
                <Markdown>{props.reasoning}</Markdown>
            </Grid>
            {props.feedback && (
                <React.Fragment>
                    <AcxLLMReasoningFeedbackDivider />
                    <AcxLLMReasoningFeedback {...props.feedback} />
                </React.Fragment>
            )}
        </Grid>
    );
}

type AcxViewLLMReasoningButtonProps = Partial<
    Omit<
        React.ComponentPropsWithoutRef<typeof AcxGradientTypography>,
        "children"
    >
>;

export function AcxViewLLMReasoningButton(
    props: AcxViewLLMReasoningButtonProps,
) {
    return (
        <AcxGradientTypography
            textAlign="center"
            {...props}
            role="button"
            sx={[
                { cursor: "pointer" },
                ...(Array.isArray(props.sx) ? props.sx : [props.sx]),
            ]}
        >
            View Reasoning <SmallSparklingStar />
        </AcxGradientTypography>
    );
}

function SmallSparklingStar() {
    return (
        <svg
            xmlns="http://www.w3.org/2000/svg"
            width="8"
            height="8"
            viewBox="0 0 8 8"
            fill="none"
        >
            <path
                d="M7.5322 4.25859L6.03835 5.00475C5.58912 5.22782 5.22758 5.59244 5.00297 6.04013L4.25681 7.53244C4.15066 7.74475 3.84604 7.74475 3.73989 7.53244L2.99373 6.04013C2.77066 5.59244 2.40604 5.22936 1.95835 5.00475L0.46758 4.25859C0.255273 4.15244 0.255273 3.84782 0.46758 3.74167L1.96143 2.99552C2.41066 2.77244 2.7722 2.40782 2.99681 1.96013L3.74143 0.467825C3.84758 0.255517 4.1522 0.255517 4.25835 0.467825L5.0045 1.96013C5.22758 2.40782 5.5922 2.7709 6.03989 2.99552L7.53373 3.74167C7.74604 3.84782 7.74604 4.15244 7.53373 4.25859H7.5322Z"
                fill="url(#paint0_linear_24129_21338)"
            />
            <defs>
                <linearGradient
                    id="paint0_linear_24129_21338"
                    x1="0.527385"
                    y1="0.600483"
                    x2="8.14721"
                    y2="7.24892"
                    gradientUnits="userSpaceOnUse"
                >
                    <stop stopColor="#32D1B7" />
                    <stop offset="0.695" stopColor="#349BC7" />
                    <stop offset="1.0001" stopColor="#3664D6" />
                </linearGradient>
            </defs>
        </svg>
    );
}
